import { apiClient } from '@/lib/api/client'

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical' | 'unknown'
export type AdminReviewStatus = 'pending_review' | 'approved' | 'rejected'

export interface FlaggedArtwork {
  analysis_id: number
  artwork_id: number
  admin_status: AdminReviewStatus
  admin_reviewed_at: string | null
  admin_notes: string | null
  analysis_date: string
  risk_level: RiskLevel | null
  risk_confidence: number | null
  risk_reasoning: string | null
  title: string | null
  file_name: string | null
  artist_id: number
  artist_name: string | null
  artistic_name: string | null
  reviewed_by_name: string | null
}

export interface FlaggedArtworksResponse {
  artworks: FlaggedArtwork[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export type SortKey = 'analysis_date' | 'risk' | 'confidence' | 'title'

export interface AnalysisStats {
  funnel: {
    pending_total: number
    analyzed: number
    unassessed: number
    auto_publish_eligible: number
    blocked_by_risk: number
    blocked_by_review: number
  }
  riskDistribution: { risk_level: string; count: number }[]
  reviewDistribution: { admin_status: string; count: number }[]
  throughput: { date: string; count: number }[]
  confidence: { avg_confidence: string | null; scored: number }
  autoPublishEnabled: boolean
  safeRiskLevels: string[]
}

export interface PublishReadiness {
  copyrightEligible: number
  truncated: boolean
  readiness: { any_variant: number; classic_frame_ok: number; print_ok: number }
  publishable: number
  blockedByCatalogue: number
  velocity: {
    pending_total: number
    draft_total: number
    published_last_7d: number
    published_last_30d: number
  }
  publishedRecently: { date: string; count: number }[]
  autoPublishEnabled: boolean
  batchSize: number
}

export interface PublishPipeline {
  byStatus: { status: string; count: number }[]
  autoSweep: { total: number; completed: number; failed: number; last_run_at: string | null }
  failureReasons: { reason: string; count: number; last_seen: string }[]
  recentFailures: {
    artwork_id: number
    title: string | null
    created_at: string
    error_message: string
  }[]
  stuckCount: number
  timing: { avg_seconds: number | null; median_seconds: number | null; sampled: number }
  publishedTotal: number
  autoPublishEnabled: boolean
}

export interface VisionPage {
  url: string
  pageTitle?: string | null
  fullMatchingImages?: string[]
  partialMatchingImages?: string[]
}

export interface AnalysisDetails {
  id: number
  artwork_id: number
  version: number
  title: string | null
  file_name: string | null
  artist_name: string | null
  artistic_name: string | null
  admin_status: AdminReviewStatus
  admin_notes: string | null
  created_at: string
  canonical_metadata: {
    inferredTitle?: string
    style?: string
    medium?: string
    tags?: string[]
  } | null
  copyright_assessment: {
    riskLevel?: RiskLevel
    reasoning?: string
    confidence?: number
  } | null
  competitive_presence: {
    saturationLevel?: string
    insights?: string
    marketplaceFindings?: { platform?: string; similarCount?: number; url?: string }[]
  } | null
  sources: { url: string; type?: string }[] | null
  llm_diagnostics: { model?: string; confidence?: number; reasoning?: string } | null
  vision_raw_response: {
    bestGuessLabels?: string[]
    webEntities?: { description?: string; score?: number }[]
    labels?: { description?: string; score?: number }[]
    fullMatchingImages?: string[]
    partialMatchingImages?: string[]
    visuallySimilarImages?: string[]
    pagesWithMatchingImages?: VisionPage[]
  } | null
}

export const analysisApi = {
  getDetails: async (analysisId: number): Promise<AnalysisDetails> => {
    const response = await apiClient.get<{ success: boolean; data: AnalysisDetails }>(
      `/api/admin/analysis/${analysisId}/details`
    )
    return response.data.data
  },

  getReadiness: async (): Promise<PublishReadiness> => {
    const response = await apiClient.get<{ success: boolean; data: PublishReadiness }>(
      '/api/admin/analysis/readiness'
    )
    return response.data.data
  },

  getPipeline: async (): Promise<PublishPipeline> => {
    const response = await apiClient.get<{ success: boolean; data: PublishPipeline }>(
      '/api/admin/analysis/pipeline'
    )
    return response.data.data
  },

  getStats: async (): Promise<AnalysisStats> => {
    const response = await apiClient.get<{ success: boolean; data: AnalysisStats }>(
      '/api/admin/analysis/stats'
    )
    return response.data.data
  },

  getFlagged: async (params: {
    page?: number
    limit?: number
    riskLevel?: string
    adminStatus?: string
    artistId?: number
    search?: string
    sortBy?: SortKey
    sortDir?: 'asc' | 'desc'
  }): Promise<FlaggedArtworksResponse> => {
    const response = await apiClient.get<{ success: boolean; data: FlaggedArtworksResponse }>(
      '/api/admin/analysis/flagged',
      { params }
    )
    return response.data.data
  },

  approve: async (analysisId: number, notes?: string) => {
    const response = await apiClient.post(`/api/admin/analysis/${analysisId}/approve`, { notes })
    return response.data
  },

  reject: async (analysisId: number, notes: string) => {
    const response = await apiClient.post(`/api/admin/analysis/${analysisId}/reject`, { notes })
    return response.data
  },
}
